import React, { useEffect, useState } from "react";

import { Form, Formik } from "formik";
import { useHistory, useRouteMatch } from "react-router-dom";

import { Button } from "Components/Button";
import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import PageHeader from "Components/Pages/Header";
import PageWrapper from "Components/Pages/Wrapper";
import Breadcrumb from "Components/Shared/Breadcrumb";
import { LoadingText } from "Components/LoadingText";
import { UnitsRegionPicker } from "Components/Units/UnitsRegionPicker";

import { useRegions } from "Hooks/useRegions";
import { useSelectUnits } from "Hooks/useSelectUnits";
import { UnitsForm } from "Hooks/useSelectUnits/interface";

import { formatUnitsRequest } from "Pages/Product/ProductForm/utils";

import {
  createUnitHoliday,
  getUnitHoliday,
  updateUnitHoliday,
} from "Requests/Units";

import { createToast } from "Utils/toastFunc";
import { Box } from "styles/globalComponents";
import { getInitialUnitsValue } from "Utils/Units/Units";

const SCOPE_OPTIONS = [
  { id: "nacional", name: "Nacional" },
  { id: "municipal", name: "Municipal" },
  { id: "state", name: "Estadual" },
] as const;

export type UnitAvailabilityValues = {
  availabilityDate: string;
  description: string;
  scope: "nacional" | "municipal";
  units: UnitsForm;
  is_fixed?: boolean;
};

const INITIAL_VALUES: UnitAvailabilityValues = {
  availabilityDate: "",
  description: "",
  scope: "nacional",
  units: {
    include: [0],
    exclude: [],
  },
};

function toDateInputValue(value: string | undefined) {
  if (!value) return "";
  return String(value).trim().split(" ")[0].split("T")[0];
}

function apiScopeToUi(scope: string | undefined): "nacional" | "municipal" {
  const normalized = String(scope ?? "").toLowerCase();
  if (normalized === "national") return "nacional";
  return "municipal";
}

function uiScopeToApi(
  scope: "nacional" | "municipal"
): "national" | "municipal" {
  return scope === "nacional" ? "national" : "municipal";
}

export function UnitAvailability() {
  const history = useHistory();
  const editMatch = useRouteMatch<{ holidayId: string }>({
    path: "/unidades/disponibilidade/:holidayId/editar",
    exact: true,
  });
  const holidayId = editMatch?.params?.holidayId;

  const { regions, loadingRegions, regionsError } = useRegions();
  const { selectAllUnits, selectRegion, selectUnit } = useSelectUnits();

  const [initialValues, setInitialValues] =
    useState<UnitAvailabilityValues>(INITIAL_VALUES);
  const [formReady, setFormReady] = useState(() => !holidayId);

  async function loadHoliday() {
    setFormReady(false);
    try {
      const res = await getUnitHoliday(holidayId);
      const h = res?.data as
        | {
            description?: string;
            holiday?: string;
            scope?: string;
            units?: UnitsForm;
            is_fixed?: boolean;
          }
        | undefined;

      setInitialValues({
        availabilityDate: toDateInputValue(h?.holiday),
        description: h?.description ?? "",
        scope: apiScopeToUi(h?.scope ?? ""),
        units: getInitialUnitsValue(h?.units, regions),
        is_fixed: h?.is_fixed,
      });
    } catch {
      createToast("error", "Não foi possível carregar o feriado.");
      history.push("/unidades/disponibilidade");
    } finally {
      setFormReady(true);
    }
  }

  const handleSubmit = async (
    values: UnitAvailabilityValues,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    if (!values.availabilityDate) {
      createToast("error", "Informe a data.");
      setSubmitting(false);
      return;
    }

    if (!values.description.trim()) {
      createToast("error", "Informe a descrição.");
      setSubmitting(false);
      return;
    }

    if (!values.units.include.length) {
      createToast("error", "Selecione ao menos uma unidade.");
      setSubmitting(false);
      return;
    }

    const unitsFormatted = formatUnitsRequest(values.units, regions);

    if (!unitsFormatted) {
      createToast(
        "error",
        "Não foi possível montar a seleção de unidades. Tente novamente."
      );
      setSubmitting(false);
      return;
    }

    const payload = {
      description: values.description.trim(),
      holiday: values.availabilityDate,
      scope: uiScopeToApi(values.scope),
      units: unitsFormatted,
    };

    try {
      const res = holidayId
        ? await updateUnitHoliday(holidayId, payload)
        : await createUnitHoliday(payload);

      if (res?.status === 200 || res?.status === 201) {
        createToast(
          "success",
          holidayId
            ? "Feriado atualizado com sucesso."
            : "Feriado criado com sucesso."
        );
        history.push("/unidades/disponibilidade");
        return;
      }

      createToast("error", "Não foi possível salvar o feriado.");
    } catch {
      createToast("error", "Não foi possível salvar o feriado.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!holidayId) {
      setInitialValues(INITIAL_VALUES);
      setFormReady(true);
      return;
    }

    if (regions) loadHoliday();
  }, [holidayId, history, regions]);

  const pageTitle = holidayId ? "Editar feriado" : "Novo feriado";

  if (!formReady) {
    return (
      <PageWrapper className="entry-app-content">
        <div className="content-holder">
          <LoadingText text="Carregando feriado" />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper className="entry-app-content">
      <div className="content-holder">
        <div className="page-heading">
          <div className="wrapper-header-content">
            <PageHeader title={pageTitle}>
              <Breadcrumb
                list={[
                  {
                    path: "/unidades",
                    label: "Unidades",
                    current: false,
                  },
                  {
                    path: "/unidades/disponibilidade",
                    label: "Disponibilidade",
                    current: false,
                  },
                  {
                    path: "#",
                    label: pageTitle,
                    current: true,
                  },
                ]}
              />
            </PageHeader>
          </div>
        </div>

        <div className="page-content">
          <Formik<UnitAvailabilityValues>
            enableReinitialize
            initialValues={initialValues}
            onSubmit={async (values, { setSubmitting }) => {
              handleSubmit(values, setSubmitting);
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => {
              const handleSelectAllUnits = () => {
                setFieldValue("units", selectAllUnits(values.units));
              };

              const handleSelectRegion = (regionId: number) => {
                setFieldValue("units", selectRegion(regionId, values.units));
              };

              const handleSelectUnit = (unitId: number) => {
                setFieldValue("units", selectUnit(unitId, values.units));
              };

              return (
                <Form>
                  <div className="row">
                    <div className="md-4">
                      <Input
                        id="availability-date"
                        type="date"
                        label="Data"
                        value={values.availabilityDate}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue("availabilityDate", event.target.value);
                        }}
                        placeholder=""
                        error=""
                        required={false}
                        purple={false}
                        multiline={false}
                        onBlur={undefined}
                        onFocus={undefined}
                        mask=""
                        maskChar=""
                        maxLength={undefined}
                        min={undefined}
                        description=""
                        small={false}
                        readOnly={values.is_fixed}
                        onKeyPress={undefined}
                      />
                    </div>
                    <div className="md-4">
                      <Select
                        id="availability-scope"
                        readOnly={values.is_fixed}
                        label="Escopo"
                        value={values.scope}
                        options={[...SCOPE_OPTIONS]}
                        optionName="name"
                        placeholder=""
                        error=""
                        onChange={(
                          event: React.ChangeEvent<HTMLSelectElement>
                        ) => {
                          const nextScope = event.target.value as
                            | "nacional"
                            | "municipal";
                          setFieldValue("scope", nextScope);
                        }}
                      />
                    </div>
                  </div>

                  <Box className="m-bottom-2" mb="20" mt="20">
                    <Input
                      id="availability-description"
                      type="text"
                      label="Descrição"
                      placeholder="Observações sobre a disponibilidade"
                      value={values.description}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue("description", event.target.value);
                      }}
                      maxLength={500}
                      error=""
                      required={false}
                      purple={false}
                      multiline={false}
                      onBlur={undefined}
                      onFocus={undefined}
                      mask=""
                      maskChar=""
                      min={undefined}
                      description=""
                      small={false}
                      readOnly={values.is_fixed}
                      onKeyPress={undefined}
                    />
                  </Box>

                  <UnitsRegionPicker
                    regions={regions}
                    loadingRegions={loadingRegions}
                    regionsError={regionsError}
                    onSelectAllUnits={handleSelectAllUnits}
                    onSelectRegion={handleSelectRegion}
                    onSelectUnit={handleSelectUnit}
                  />

                  <Box
                    className="m-top-2d"
                    mt="40"
                    pb="50"
                    display="flex"
                    justify="center"
                  >
                    <Button type="submit" disabled={isSubmitting}>
                      Salvar
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </PageWrapper>
  );
}
